const ROLE_PREFIX = 'ROLE_';

function decodeBase64Url(segment: string): string {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  return atob(normalized + padding);
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const json = decodeBase64Url(parts[1]);
    const payload = JSON.parse(json) as unknown;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return null;
    }
    return payload as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function normalizeRoleAuthority(role: string): string {
  const trimmed = role.trim();
  if (!trimmed) {
    return trimmed;
  }
  return trimmed.startsWith(ROLE_PREFIX) ? trimmed : `${ROLE_PREFIX}${trimmed}`;
}

function collectRoleStrings(value: unknown, roles: Set<string>): void {
  if (!Array.isArray(value)) {
    return;
  }

  for (const item of value) {
    if (item == null) {
      continue;
    }
    const role = String(item).trim();
    if (role) {
      roles.add(normalizeRoleAuthority(role));
    }
  }
}

function collectRealmRoles(payload: Record<string, unknown>, roles: Set<string>): void {
  const realmAccess = payload['realm_access'];
  if (!realmAccess || typeof realmAccess !== 'object' || Array.isArray(realmAccess)) {
    return;
  }

  collectRoleStrings((realmAccess as Record<string, unknown>)['roles'], roles);
}

function collectResourceAccessRoles(payload: Record<string, unknown>, roles: Set<string>): void {
  const resourceAccess = payload['resource_access'];
  if (!resourceAccess || typeof resourceAccess !== 'object' || Array.isArray(resourceAccess)) {
    return;
  }

  for (const clientAccess of Object.values(resourceAccess)) {
    if (!clientAccess || typeof clientAccess !== 'object' || Array.isArray(clientAccess)) {
      continue;
    }
    collectRoleStrings((clientAccess as Record<string, unknown>)['roles'], roles);
  }
}

function collectTopLevelRoles(payload: Record<string, unknown>, roles: Set<string>): void {
  collectRoleStrings(payload['roles'], roles);
}

export function extractRolesFromJwtPayload(payload: Record<string, unknown>): string[] {
  const roles = new Set<string>();
  collectRealmRoles(payload, roles);
  collectResourceAccessRoles(payload, roles);
  collectTopLevelRoles(payload, roles);
  return [...roles];
}

export function getRolesFromAccessToken(token: string | null): string[] {
  if (!token) {
    return [];
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return [];
  }

  return extractRolesFromJwtPayload(payload);
}
